import React, { useEffect, useState } from 'react';
import { Modal } from '@ver-uds/react';
import {
  Alert,
  Button,
  ButtonGroup,
  RadioButton,
  ErrorMessage,
  FormGroup,
  Fieldset,
  Legend,
  Checkbox,
  Icon,
} from '@ver-uds/uswds-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createBrowserHistory } from 'history';
import { useHistory } from 'react-router-dom';
import EveRoutes from '../EveRoutes';
import useCaseCreationPreference, {
  CaseCreationPreferenceOptions,
} from './CaseCreationPreference/CreateCasePreference';

export interface EveCreateCaseModalProps {
  open: boolean;
  close(): void;
  refresh: boolean;
  enableFederalContractorHelpText: boolean;
}

const EveCreateCaseModal = ({
  open,
  close,
  refresh,
  enableFederalContractorHelpText = false,
}: EveCreateCaseModalProps): JSX.Element => {
  const historyRefresh = createBrowserHistory({ forceRefresh: true });
  const history = useHistory();
  const [errors, setErrors] = useState(false);
  const [userSelection, setUserSelection] = useState(CaseCreationPreferenceOptions.CreateCasePromptUser);
  const [, setCreatePreference] = useCaseCreationPreference();
  const [rememberPreference, setRememberPreference] = useState(false);

  const [showExpandedFederalContractorHelpText, setShowExpandedFederalContractorHelpText] = useState(false);

  // Fixes an issue where userSelection persisted after the modal was closed.
  // This would cause the remember checkbox to show when it shouldn't
  useEffect(() => {
    if (open) {
      return;
    }

    setUserSelection(CaseCreationPreferenceOptions.CreateCasePromptUser);
  }, [open]);

  const navigate = (location: string) => {
    if (refresh) {
      historyRefresh.replace(location);
    } else {
      history.push(location);
    }
  };

  const savePreference = () => {
    if (rememberPreference) {
      setCreatePreference(userSelection);
    }
  };

  const submitForm = () => {
    if (
      userSelection !== CaseCreationPreferenceOptions.CreateCaseUsingEVerifyPlus &&
      userSelection !== CaseCreationPreferenceOptions.CreateCaseUsingEVerify
    ) {
      setErrors(true);
    } else {
      setErrors(false);
      savePreference();
      if (userSelection === CaseCreationPreferenceOptions.CreateCaseUsingEVerifyPlus) {
        navigate(EveRoutes.CASES_DRAFT);
      } else {
        navigate(EveRoutes.CASES_NEW);
      }
    }
  };

  const handlePreferenceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as CaseCreationPreferenceOptions;
    setUserSelection(value);
  };

  const handleRememberPreferenceChange = () => {
    setRememberPreference(!rememberPreference);
  };

  return (
    <Modal id="create-new-case-modal" header="Create New Case" open={open} close={close} fixedToTop>
      <Modal.Content>
        <Alert status="info">
          <Alert.Heading>Modernize the verification process with E-Verify+</Alert.Heading>
          <Alert.Text>
            E-Verify+ streamlines the employment eligibility process by having employees complete their Form I-9
            directly through E-Verify. This lessens your administrative burden and gives employees greater control over
            their personal information.
          </Alert.Text>
        </Alert>
        <FormGroup error={errors} className={errors ? 'margin-left-05' : ''}>
          <Fieldset>
            <Legend>How will you verify this employee?</Legend>
            {enableFederalContractorHelpText && (
              <div
                className="text-base margin-top-05 cursor-pointer"
                role="button"
                tabIndex={-1}
                onClick={() => setShowExpandedFederalContractorHelpText(!showExpandedFederalContractorHelpText)}
                onKeyDown={() => setShowExpandedFederalContractorHelpText(!showExpandedFederalContractorHelpText)}
              >
                Important information regarding federal contractors with FAR E-Verify clause{' '}
                <Icon
                  name={showExpandedFederalContractorHelpText ? 'expand_less' : 'expand_more'}
                  className="top-2px"
                />
              </div>
            )}
            {showExpandedFederalContractorHelpText && (
              <div className="text-base margin-top-05 margin-bottom-3">
                For employers who are federal contractors whose contracts contain the FAR E-Verify Clause:
                <ul className="margin-y-0">
                  <li>
                    You may choose either selection if your new or existing employee needs to complete a new Form I-9.
                  </li>
                  <li>
                    Choose the second section if your employee already completed Form I-9, OR updated an existing
                    employee’s original Form I-9.
                  </li>{' '}
                </ul>
                <p className="margin-top-1">
                  Check the supplemental Guide for Federal Contractors at{' '}
                  <a
                    className="usa-link"
                    href="https://www.e-verify.gov/supplemental-guide-for-federal-contractors"
                    target="_blank"
                    aria-label="Open Guide for Federal Contractors in a new tab"
                    rel="noreferrer"
                  >
                    https://www.e-verify.gov/supplemental-guide-for-federal-contractors
                  </a>{' '}
                  for more information.
                </p>
              </div>
            )}
            {errors && (
              <ErrorMessage id="create-case-action-error" className="margin-bottom-2 margin-top-neg-1">
                A selection is required to continue.
              </ErrorMessage>
            )}
            <div className="margin-top-1">
              <RadioButton
                id="submit-documents-electronically"
                label="Employee will submit their information and documents electronically using E-Verify+."
                name="createCaseAction"
                value={CaseCreationPreferenceOptions.CreateCaseUsingEVerifyPlus}
                onChange={handlePreferenceChange}
                required
              />

              <RadioButton
                id="present-documents-in-person"
                label="Employee has completed their Form I-9. I will submit their information using E-Verify."
                name="createCaseAction"
                value={CaseCreationPreferenceOptions.CreateCaseUsingEVerify}
                onChange={handlePreferenceChange}
                required
              />
            </div>
          </Fieldset>
          {(userSelection === CaseCreationPreferenceOptions.CreateCaseUsingEVerifyPlus ||
            userSelection === CaseCreationPreferenceOptions.CreateCaseUsingEVerify) && (
            <Fieldset>
              <div className="margin-top-3">
                <Checkbox
                  onChange={handleRememberPreferenceChange}
                  id="remember-create-case-preference"
                  label="Remember my choice for next time"
                  name="app"
                />
                {rememberPreference && (
                  <Alert status="info" slim>
                    <Alert.Text>
                      You can always modify your verification process preference under &apos;User Profile&apos;.
                    </Alert.Text>
                  </Alert>
                )}
              </div>
            </Fieldset>
          )}
        </FormGroup>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button onClick={close} type="button" variant="outline">
            Cancel
          </Button>
          <Button onClick={() => submitForm()} type="button" variant="primary">
            Continue
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default EveCreateCaseModal;
